import { useCallback, useEffect, useState } from 'react'

import { logger } from 'src/utils/logger'

import { getOmni } from '../apiHelper/omniApi'
import { getFromSettingsServer } from '../apiHelper/settingsApi'

/**
 * Custom hook to determine if the "Link to SSO" menu option should be visible
 *
 * This hook encapsulates the logic for checking SSO link visibility by:
 * 1. Validating the practice has SSO configured (non-empty sso_tenant_key)
 * 2. Verifying the tenant is valid and SSO is enabled (via API call)
 * 3. Checking if user already has SSO account linked to this tenant
 * 4. Listening for SSO linking completion events to refresh visibility
 *
 * The hook automatically rechecks visibility when:
 * - The practice object changes
 * - The 'sso-linking-complete' custom event is dispatched
 *
 * @param {Object} practice - The current practice object from Redux state
 * @param {string} practice.sso_tenant_key - The SSO tenant key for the practice
 *
 * @returns {boolean} showSsoLinking - Whether to display the "Link to SSO" menu option
 *
 * @example
 * function SettingsDropdown() {
 *   const practice = useSelector((state) => state.user?.practice)
 *   const showSsoLinking = useSsoLinkVisibility(practice)
 *
 *   return (
 *     {showSsoLinking && (
 *       <CDropdownItem onClick={handleLinkToSso}>
 *         Link to SSO
 *       </CDropdownItem>
 *     )}
 *   )
 * }
 */
export function useSsoLinkVisibility(practice) {
  const [showSsoLinking, setShowSsoLinking] = useState(false)

  /**
   * Checks if the "Link to SSO" menu option should be displayed to the user
   *
   * @async
   * @returns {Promise<void>} Updates showSsoLinking state, no return value
   * @throws Will catch and log errors, defaulting to hiding the menu item on failure
   */
  const checkSsoLinkVisibility = useCallback(async () => {
    logger.log('[SSO Linking] Checking visibility...')

    // Check if practice has SSO tenant key
    const tenantKey = practice?.sso_tenant_key
    logger.log('[SSO Linking] Tenant key:', tenantKey)

    if (!tenantKey || tenantKey.trim() === '') {
      logger.log('[SSO Linking] No tenant key found, hiding menu item')
      setShowSsoLinking(false)
      return
    }

    try {
      // Verify the tenant is valid and SSO is enabled
      logger.log('[SSO Linking] Verifying tenant with API...')
      const response = await getOmni(`/sso/client/verify/${encodeURIComponent(tenantKey)}`)
      logger.log('[SSO Linking] API response:', response)

      if (!response?.is_valid) {
        logger.log('[SSO Linking] Tenant invalid:', {
          is_valid: response?.is_valid,
        })
        setShowSsoLinking(false)
        return
      }

      // Check if user already has SSO linked to this tenant
      logger.log('[SSO Linking] Checking if user already has SSO linked...')
      try {
        const userInfo = await getFromSettingsServer('currentuser')
        logger.log('[SSO Linking] User info:', userInfo)

        if (userInfo?.sso_linkage && userInfo.sso_linkage.sso_tenant_key === tenantKey) {
          logger.log('[SSO Linking] User already has SSO linked to this tenant, hiding menu item')
          setShowSsoLinking(false)
          return
        }

        logger.log('[SSO Linking] User does not have SSO linked, showing menu item')
        setShowSsoLinking(true)
      } catch (userInfoError) {
        logger.error('[SSO Linking] Error fetching user info:', userInfoError)
        // If we can't fetch user info, show the link option anyway
        // (user might not have it linked and API just failed)
        setShowSsoLinking(true)
      }
    } catch (error) {
      logger.error('[SSO Linking] Error checking SSO availability:', error)
      setShowSsoLinking(false)
    }
  }, [practice])

  // Check visibility when practice changes
  useEffect(() => {
    logger.log('[SSO Linking] Practice changed:', practice)
    logger.log('[SSO Linking] Practice sso_tenant_key:', practice?.sso_tenant_key)
    checkSsoLinkVisibility()
  }, [practice, checkSsoLinkVisibility])

  // Listen for SSO linking completion event
  useEffect(() => {
    const handleSsoLinkingComplete = () => {
      logger.log('[SSO Linking] Received sso-linking-complete event, re-checking visibility')
      checkSsoLinkVisibility()
    }

    window.addEventListener('sso-linking-complete', handleSsoLinkingComplete)

    return () => {
      window.removeEventListener('sso-linking-complete', handleSsoLinkingComplete)
    }
  }, [checkSsoLinkVisibility])

  return showSsoLinking
}
